import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useLocation, useNavigate } from 'react-router-dom';
import "./GetCoupon.css";
import PaymentProvider from '../payment/PaymentProvider';
import { useTranslation } from 'react-i18next';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const serverUrl = "/api";

const CheckoutForm = ({ name, price, gameId, mail }) => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [discountCode, setDiscountCode] = useState(() => {
    return localStorage.getItem('applied_discount_code') || '';
  });
  const [discountCodeId, setDiscountCodeId] = useState(null);
  const [discountedPrice, setDiscountedPrice] = useState(price);
  const [discountError, setDiscountError] = useState(null);
  const [discountSuccess, setDiscountSuccess] = useState(false);
  const basePrice = price;

  const applyDiscount = async (codeToApply) => {
    const code = typeof codeToApply === 'string' ? codeToApply : discountCode;
    setDiscountError(null);
    setDiscountSuccess(false);
    if (!code || !code.trim()) return;
    try {
      const response = await fetch(serverUrl + '/validate-discount', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim(), gameId }),
      });
      const data = await response.json();
      if (data.valid) {
        const reduction = (basePrice * data.reduction_percent) / 100;
        const newPrice = Math.max(0, basePrice - reduction).toFixed(2);
        setDiscountedPrice(newPrice);
        setDiscountCodeId(data.discount_code_id);
        setDiscountSuccess(true);
      } else {
        setDiscountError(data.message || t('invalid_discount_code'));
        setDiscountedPrice(basePrice);
        setDiscountCodeId(null);
      }
    } catch (e) {
      setDiscountError(t('error_validating_discount'));
    }
  };

  useEffect(() => {
    const savedCode = localStorage.getItem('applied_discount_code');
    if (savedCode && savedCode.trim()) {
      applyDiscount(savedCode.trim());
    }
  }, [gameId, basePrice]);

  const handlePayment = async () => {
    if (!stripe || !elements) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(serverUrl + '/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: discountedPrice }),
      });
      const { clientSecret } = await response.json();

      const token = localStorage.getItem('token');
      const billingDetails = token?.trim()
        ? { name: token }
        : { email: mail };

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: billingDetails,
        },
      });

      if (result.error) {
        setError(`Payment failed: ${result.error.message}`);
      } else {
        const provider = new PaymentProvider();
        const data = await provider.createCoupon(token, gameId, discountedPrice, mail || "", discountCodeId, basePrice);
        if (data) {
          navigate('/success', { state: data, replace: true });
        } else {
          setError("Problem creating new coupon");
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('Error processing payment');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="checkout-form-pay">
      <h2>{name}</h2>
      {discountCodeId ? (
        <p className="price">
          <span style={{ textDecoration: 'line-through', color: '#888', marginRight: '10px' }}>{basePrice} {t('eur')}</span>
          <span>{t('price')}: {discountedPrice} {t('eur')}</span>
        </p>
      ) : (
        <p className="price">{t('price')}: {basePrice} {t('eur')}</p>
      )}

      <div className="discount-section" style={{ marginBottom: '20px' }}>
        <input 
          type="text" 
          placeholder={t('discount_code_placeholder', 'Discount Code')} 
          value={discountCode} 
          onChange={(e) => setDiscountCode(e.target.value)} 
          style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc', marginRight: '10px', width: '60%' }}
        />
        <button onClick={applyDiscount} style={{ padding: '10px 15px', borderRadius: '5px', backgroundColor: '#a239ca', color: '#fff', border: 'none', cursor: 'pointer' }}>
          {t('apply')}
        </button>
        {discountError && <p className="error" style={{ fontSize: '12px', marginTop: '5px' }}>{discountError}</p>}
        {discountSuccess && <p style={{ color: 'green', fontSize: '12px', marginTop: '5px' }}>{t('discount_applied', 'Discount applied successfully!')}</p>}
      </div>

      <CardElement className="card-element" />
      <button className="payment-button" onClick={handlePayment} disabled={isLoading}>
        {isLoading ? <div className="spinner"></div> : t('buy_coupon')}
      </button>
      {error && <p className="error">{error}</p>}
      <p className="secure-info">🔒 {t('your_payment_is_secure_with_stripe')}</p>
    </div>
  );
};

const GetCoupon = () => {
  const { t } = useTranslation();
  const [gameData, setGameData] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const { state } = location;
    setGameData(state);
  }, [location]);

  if (!gameData) return <div>{t('loading')}...</div>;

  return (
    <div className="get-coupon">
      <Elements stripe={stripePromise}>
        <CheckoutForm
          name={gameData.name}
          price={gameData.price}
          gameId={gameData.gameId}
          mail={gameData.mail}
        />
      </Elements>
    </div>
  );
};

export default GetCoupon;
